import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RuleSettingsPage() {
  const { fleetId } = useParams();
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState({
    alert_a1_count: 2,
    alert_a2_count: 2,
    alert_a3_count: 2,
    a1_block_min_days: 3,
    a1_block_max_days: 7,
    a2_block_min_days: 3,
    a2_block_max_days: 7,
    weekend_duty_type: 'Blank',
    weekday_default: 'OD',
    buffer_a3_before_do: true,
    buffer_days: 2,
    enable_riyadh_break: true,
    riyadh_break_start_day: 14,
    riyadh_break_duration: 3,
    max_consecutive_duty_days: 7,
    min_rest_days_after_duty: 2,
    ai_instructions: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [fleetRes, rulesRes] = await Promise.all([
        axios.get(`${API_URL}/api/fleets/${fleetId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rule-settings/${fleetId}`, { withCredentials: true })
      ]);
      setFleet(fleetRes.data);
      setRules(rulesRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [fleetId, setFleet, setRules, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/rule-settings/${fleetId}`, rules, { withCredentials: true });
      toast.success('Rules saved successfully');
    } catch (error) {
      toast.error('Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate(`/fleet/${fleetId}/schedule`)} className="p-2 hover:bg-gray-100 rounded-sm">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-[#111827]">{fleet?.name} - Rule Settings</h1>
            <p className="text-sm text-[#6B7280] mt-1">Configure scheduling rules and parameters</p>
          </div>
        </div>

        <form onSubmit={handleSave} className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-6">
          {/* Alert Pairing Rules */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#111827] mb-4 pb-2 border-b border-[#E5E7EB]">Alert Pairing Rules</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">A1 Count per Cycle</label>
                <input
                  type="number"
                  value={rules.alert_a1_count}
                  onChange={(e) => setRules({ ...rules, alert_a1_count: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">A2 Count per Cycle</label>
                <input
                  type="number"
                  value={rules.alert_a2_count}
                  onChange={(e) => setRules({ ...rules, alert_a2_count: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">A3 Count per Cycle</label>
                <input
                  type="number"
                  value={rules.alert_a3_count}
                  onChange={(e) => setRules({ ...rules, alert_a3_count: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="10"
                />
              </div>
            </div>
          </div>

          {/* Duty Block Caps */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#111827] mb-4 pb-2 border-b border-[#E5E7EB]">Duty Block Caps</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">A1 Block Min Days</label>
                <input
                  type="number"
                  value={rules.a1_block_min_days}
                  onChange={(e) => setRules({ ...rules, a1_block_min_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">A1 Block Max Days</label>
                <input
                  type="number"
                  value={rules.a1_block_max_days}
                  onChange={(e) => setRules({ ...rules, a1_block_max_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">A2 Block Min Days</label>
                <input
                  type="number"
                  value={rules.a2_block_min_days}
                  onChange={(e) => setRules({ ...rules, a2_block_min_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">A2 Block Max Days</label>
                <input
                  type="number"
                  value={rules.a2_block_max_days}
                  onChange={(e) => setRules({ ...rules, a2_block_max_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="31"
                />
              </div>
            </div>
          </div>

          {/* Weekend & Weekday Rules */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#111827] mb-4 pb-2 border-b border-[#E5E7EB]">Weekend & Weekday Rules</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Weekend Duty Type</label>
                <select
                  value={rules.weekend_duty_type}
                  onChange={(e) => setRules({ ...rules, weekend_duty_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                >
                  <option value="Blank">Blank</option>
                  <option value="DO">DO</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Weekday Default Duty</label>
                <select
                  value={rules.weekday_default}
                  onChange={(e) => setRules({ ...rules, weekday_default: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                >
                  <option value="OD">OD</option>
                  <option value="A1">A1</option>
                  <option value="A2">A2</option>
                  <option value="A3">A3</option>
                </select>
              </div>
            </div>
          </div>

          {/* Buffer Rules */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#111827] mb-4 pb-2 border-b border-[#E5E7EB]">Buffer Rules</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={rules.buffer_a3_before_do}
                  onChange={(e) => setRules({ ...rules, buffer_a3_before_do: e.target.checked })}
                  className="mr-2 w-4 h-4 text-[#002FA7] rounded"
                />
                <label className="text-sm font-medium text-[#111827]">Enable A3 Buffer Before DO</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Buffer Days</label>
                <input
                  type="number"
                  value={rules.buffer_days}
                  onChange={(e) => setRules({ ...rules, buffer_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="0"
                  max="5"
                />
              </div>
            </div>
          </div>

          {/* Riyadh Break Rules */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#111827] mb-4 pb-2 border-b border-[#E5E7EB]">Riyadh Mid-Month Break</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={rules.enable_riyadh_break}
                  onChange={(e) => setRules({ ...rules, enable_riyadh_break: e.target.checked })}
                  className="mr-2 w-4 h-4 text-[#002FA7] rounded"
                />
                <label className="text-sm font-medium text-[#111827]">Enable Riyadh Break</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Start Day</label>
                <input
                  type="number"
                  value={rules.riyadh_break_start_day}
                  onChange={(e) => setRules({ ...rules, riyadh_break_start_day: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="31"
                  disabled={!rules.enable_riyadh_break}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Duration (Days)</label>
                <input
                  type="number"
                  value={rules.riyadh_break_duration}
                  onChange={(e) => setRules({ ...rules, riyadh_break_duration: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="10"
                  disabled={!rules.enable_riyadh_break}
                />
              </div>
            </div>
          </div>

          {/* General Constraints */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#111827] mb-4 pb-2 border-b border-[#E5E7EB]">General Constraints</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Max Consecutive Duty Days</label>
                <input
                  type="number"
                  value={rules.max_consecutive_duty_days}
                  onChange={(e) => setRules({ ...rules, max_consecutive_duty_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="1"
                  max="31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">Min Rest Days After Duty</label>
                <input
                  type="number"
                  value={rules.min_rest_days_after_duty}
                  onChange={(e) => setRules({ ...rules, min_rest_days_after_duty: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm"
                  min="0"
                  max="10"
                />
              </div>
            </div>
          </div>

          {/* AI Instructions */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#111827] mb-4 pb-2 border-b border-[#E5E7EB]">AI-Powered Instructions (Natural Language)</h3>
            <textarea
              value={rules.ai_instructions || ''}
              onChange={(e) => setRules({ ...rules, ai_instructions: e.target.value })}
              placeholder="Example: Give Ahmed 4 more A1 days. Ensure Riyadh crew gets mid-month break. No one works more than 5 consecutive days."
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm"
              rows={4}
            />
            <p className="text-xs text-[#6B7280] mt-2">Enter natural language instructions that will be interpreted by AI during schedule generation.</p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E7EB]">
            <button
              type="button"
              onClick={() => navigate(`/fleet/${fleetId}/schedule`)}
              className="px-4 py-2 border border-[#E5E7EB] text-[#4B5563] rounded-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a] disabled:opacity-50"
            >
              <FloppyDisk size={20} />
              {saving ? 'Saving...' : 'Save Rules'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
